import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Mail, Printer, Search, X } from 'lucide-react';

export default function ToothGuidesPage() {
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [filteredPatients, setFilteredPatients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPatients, setSelectedPatients] = useState([]);
  const [loadingPatients, setLoadingPatients] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [selectedPatientsForEmail, setSelectedPatientsForEmail] = useState([]);
  const [tempEmails, setTempEmails] = useState({}); // Store temporary emails for patients without email
  const [showEmailModal, setShowEmailModal] = useState(false);

  // Fetch patients on component mount
  useEffect(() => {
    fetchPatients();
  }, []);

  // Filter patients based on search terms
  useEffect(() => {
    // Ensure patients is always an array
    const patientsArray = Array.isArray(patients) ? patients : [];
    
    // Use searchQuery for the new search interface, fallback to searchTerm for old interface
    const activeSearchTerm = searchQuery || searchTerm;
    
    if (activeSearchTerm.trim() === '') {
      setFilteredPatients(patientsArray);
    } else {
      const filtered = patientsArray.filter(patient => {
        const searchLower = activeSearchTerm.toLowerCase();
        const firstName = patient.firstName?.toLowerCase() || '';
        const lastName = patient.lastName?.toLowerCase() || '';
        const email = patient.email?.toLowerCase() || '';
        return firstName.includes(searchLower) || 
               lastName.includes(searchLower) || 
               email.includes(searchLower);
      });
      setFilteredPatients(filtered);
    }
  }, [searchTerm, searchQuery, patients]);

  const fetchPatients = async () => {
    setLoadingPatients(true);
    try {
      const token = localStorage.getItem('dentalToken');
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      
      if (!token) {
        console.error('No authentication token found');
        return;
      }

      // Decode JWT to get practiceId
      const tokenPayload = JSON.parse(atob(token.split('.')[1]));
      const practiceId = tokenPayload.practiceId;

      if (!practiceId) {
        console.error('No practiceId found in token');
        return;
      }

      const response = await fetch(`${backendUrl}/api/practice/patients?practiceId=${practiceId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const responseData = await response.json();
        // Extract patients array from response
        const patientsArray = Array.isArray(responseData.data) ? responseData.data : [];
        setPatients(patientsArray);
        setFilteredPatients(patientsArray);
      } else {
        console.error('Failed to fetch patients:', response.status);
        setPatients([]);
        setFilteredPatients([]);
      }
    } catch (error) {
      console.error('Error fetching patients:', error);
      setPatients([]);
      setFilteredPatients([]);
    } finally {
      setLoadingPatients(false);
    }
  };

  const handleDownloadQR = () => {
    const link = document.createElement('a');
    link.href = '/tooth-guides/qr-tooth-guides.png';
    link.download = 'Tooth-Guides-QR-Code.png';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handlePrint = () => {
    // Add print class to show only QR section
    document.body.classList.add('print-mode');
    window.print();
    // Remove class after print dialog closes
    setTimeout(() => {
      document.body.classList.remove('print-mode');
    }, 1000);
  };

  const togglePatientSelection = (patientId) => {
    setSelectedPatients(prev => {
      if (prev.includes(patientId)) {
        return prev.filter(id => id !== patientId);
      } else {
        return [...prev, patientId];
      }
    });
  };

  const handleSendToSelected = async () => {
    if (selectedPatients.length === 0) {
      setEmailError('Please select at least one patient');
      return;
    }

    setEmailSending(true);
    setEmailError('');
    
    try {
      const token = localStorage.getItem('dentalToken');
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      
      if (!token) {
        setEmailError('Authentication required. Please log in again.');
        setEmailSending(false);
        return;
      }
      
      // Get selected patient emails
      const patientsArray = Array.isArray(patients) ? patients : [];
      const selectedPatientData = patientsArray.filter(p => selectedPatients.includes(p.patientId));
      const selectedEmails = selectedPatientData
        .map(p => p.email)
        .filter(email => email && email.includes('@')); // Filter out empty/invalid emails

      if (selectedEmails.length === 0) {
        setEmailError('Selected patients have no valid email addresses');
        setEmailSending(false);
        return;
      }

      console.log(`Sending emails to ${selectedEmails.length} patients:`, selectedEmails);

      // Send emails one by one
      let successCount = 0;
      let errorMessages = [];
      
      for (const email of selectedEmails) {
        try {
          const response = await fetch(`${backendUrl}/api/tooth-guides/email-qr`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ email })
          });

          if (response.ok) {
            successCount++;
            console.log(`Email sent successfully to: ${email}`);
          } else {
            const errorData = await response.text();
            console.error(`Failed to send to ${email}:`, response.status, errorData);
            errorMessages.push(`${email}: ${response.status}`);
          }
        } catch (emailError) {
          console.error(`Error sending to ${email}:`, emailError);
          errorMessages.push(`${email}: Network error`);
        }
      }

      // Show results
      if (successCount > 0) {
        setEmailSuccess(true);
        setEmailError(`Successfully sent to ${successCount} of ${selectedEmails.length} patients`);
        setSelectedPatients([]);
      } else {
        setEmailError(`Failed to send emails. Errors: ${errorMessages.join(', ')}`);
      }
      
      setTimeout(() => {
        setEmailSuccess(false);
        setEmailError('');
      }, 5000);

    } catch (error) {
      console.error('Error sending emails:', error);
      setEmailError(`Failed to send emails: ${error.message}`);
    } finally {
      setEmailSending(false);
    }
  };
  const handleSendEmailToSelected = async () => {
    if (selectedPatientsForEmail.length === 0) {
      setEmailError('Please select at least one patient');
      return;
    }

    setEmailSending(true);
    setEmailError('');
    
    try {
      const token = localStorage.getItem('dentalToken');
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      
      if (!token) {
        setEmailError('Authentication required. Please log in again.');
        setEmailSending(false);
        return;
      }
      
      // Prepare email list with temporary emails for patients without email
      const emailList = [];
      let patientsWithoutEmail = 0;
      
      for (const patient of selectedPatientsForEmail) {
        let emailToUse = patient.email;
        
        // If patient doesn't have email, use temporary email if provided
        if (!emailToUse && tempEmails[patient.id]) {
          emailToUse = tempEmails[patient.id];
        }
        
        if (emailToUse && emailToUse.includes('@')) {
          emailList.push(emailToUse);
        } else {
          patientsWithoutEmail++;
        }
      }

      if (emailList.length === 0) {
        setEmailError('No valid email addresses found. Please add email addresses for selected patients.');
        setEmailSending(false);
        return;
      }

      console.log(`Sending emails to ${emailList.length} patients:`, emailList);

      // Send emails one by one
      let successCount = 0;
      let errorMessages = [];
      
      for (const email of emailList) {
        try {
          const response = await fetch(`${backendUrl}/api/tooth-guides/email-qr`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ email })
          });

          if (response.ok) {
            successCount++;
            console.log(`Email sent successfully to: ${email}`);
          } else {
            const errorData = await response.text();
            console.error(`Failed to send to ${email}:`, response.status, errorData);
            errorMessages.push(`${email}: ${response.status}`);
          }
        } catch (emailError) {
          console.error(`Error sending to ${email}:`, emailError);
          errorMessages.push(`${email}: Network error`);
        }
      }

      // Show results
      let resultMessage = '';
      if (successCount > 0) {
        setEmailSuccess(true);
        resultMessage = `Successfully sent to ${successCount} of ${emailList.length} patients`;
        if (patientsWithoutEmail > 0) {
          resultMessage += `. ${patientsWithoutEmail} patient(s) skipped due to missing email addresses.`;
        }
        setEmailError(resultMessage);
        
        // Clear selections after successful send
        setSelectedPatientsForEmail([]);
        setTempEmails({});
      } else {
        resultMessage = `Failed to send emails. Errors: ${errorMessages.join(', ')}`;
        if (patientsWithoutEmail > 0) {
          resultMessage += `. ${patientsWithoutEmail} patient(s) had no email addresses.`;
        }
        setEmailError(resultMessage);
      }
      
      setTimeout(() => {
        setEmailSuccess(false);
        setEmailError('');
      }, 5000);

    } catch (error) {
      console.error('Error sending emails:', error);
      setEmailError(`Failed to send emails: ${error.message}`);
    } finally {
      setEmailSending(false);
    }
  };

  const handleSendToAllWithEmail = async () => {
    setEmailSending(true);
    setEmailError('');
    
    try {
      const token = localStorage.getItem('dentalToken');
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      
      if (!token) {
        setEmailError('Authentication required. Please log in again.');
        setEmailSending(false);
        return;
      }
      
      // Get all patients with valid email addresses
      const patientsArray = Array.isArray(patients) ? patients : [];
      const patientsWithEmail = patientsArray.filter(p => p.email && p.email.includes('@'));
      
      if (patientsWithEmail.length === 0) {
        setEmailError('No patients with email addresses found in database.');
        setEmailSending(false);
        return;
      }

      const emailList = patientsWithEmail.map(p => p.email);
      console.log(`Sending emails to ALL ${emailList.length} patients with email:`, emailList);

      // Send emails one by one
      let successCount = 0;
      let errorMessages = [];
      
      for (const email of emailList) {
        try {
          const response = await fetch(`${backendUrl}/api/tooth-guides/email-qr`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ email })
          });

          if (response.ok) {
            successCount++;
            console.log(`Email sent successfully to: ${email}`);
          } else {
            const errorData = await response.text();
            console.error(`Failed to send to ${email}:`, response.status, errorData);
            errorMessages.push(`${email}: ${response.status}`);
          }
        } catch (emailError) {
          console.error(`Error sending to ${email}:`, emailError);
          errorMessages.push(`${email}: Network error`);
        }
      }

      // Show results
      if (successCount > 0) {
        setEmailSuccess(true);
        setEmailError(`Successfully sent to ${successCount} of ${emailList.length} patients`);
        
        // Close modal after successful send
        setTimeout(() => {
          setShowEmailModal(false);
          setSelectedPatientsForEmail([]);
          setTempEmails({});
        }, 2000);
      } else {
        setEmailError(`Failed to send emails. Errors: ${errorMessages.join(', ')}`);
      }
      
      setTimeout(() => {
        setEmailSuccess(false);
        setEmailError('');
      }, 5000);

    } catch (error) {
      console.error('Error sending emails:', error);
      setEmailError(`Failed to send emails: ${error.message}`);
    } finally {
      setEmailSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Main Content */}
      <div className="tg-page">
        <style>{`
          :root {
            --tg-primary: #0b1726;
            --tg-primary-blue: #0f6af5;
            --tg-primary-blue-dark: #0040b3;
            --tg-bg: #f5f8ff;
            --tg-card-bg: #ffffff;
            --tg-text: #1b2433;
            --tg-muted: #7b8698;
            --tg-accent: #ff6b4a;
            --tg-radius-lg: 20px;
            --tg-radius-md: 14px;
            --tg-shadow-soft: 0 16px 40px rgba(19, 40, 80, 0.18);
          }

          .tg-page {
            max-width: 1180px;
            margin: 0 auto;
            padding: 32px 18px 40px;
            box-sizing: border-box;
          }

          .tg-page a {
            color: #ffffff;
            text-decoration: none;
          }

          .tg-page a:hover {
            text-decoration: underline;
          }

          .tg-hero {
            display: grid;
            grid-template-columns: minmax(0, 1.6fr) minmax(0, 1.4fr);
            gap: 32px;
            align-items: center;
            border-radius: 28px;
            padding: 28px 26px;
            background: radial-gradient(circle at top left, #2f8bff 0, #0f6af5 40%, #0040b3 100%);
            color: #ffffff;
            box-shadow: var(--tg-shadow-soft);
          }

          @media (max-width: 900px) {
            .tg-hero {
              grid-template-columns: 1fr;
            }
          }

          .tg-hero-badge {
            display:inline-flex;
            align-items:center;
            gap:8px;
            padding:4px 12px;
            border-radius:999px;
            background:rgba(255,255,255,0.14);
            font-size:12px;
            font-weight:600;
            letter-spacing:0.05em;
            text-transform:uppercase;
            margin-bottom:10px;
          }

          .tg-hero-badge span {
            font-size:14px;
          }

          .tg-hero-title {
            font-size:34px;
            line-height:1.15;
            margin:0 0 6px;
            font-weight:800;
          }

          .tg-hero-subtitle {
            font-size:18px;
            margin:0 0 10px;
            font-weight:500;
          }

          .tg-hero-text {
            font-size:14px;
            margin:0 0 18px;
            color:rgba(255,255,255,0.92);
            max-width:540px;
          }

          .tg-hero-cta {
            display:flex;
            flex-wrap:wrap;
            gap:10px;
            margin-bottom:12px;
          }

          .tg-btn-primary,
          .tg-btn-ghost {
            display:inline-flex;
            align-items:center;
            justify-content:center;
            padding:11px 20px;
            border-radius:999px;
            font-size:14px;
            font-weight:600;
            border:1px solid transparent;
            cursor:pointer;
            text-decoration:none;
            white-space:nowrap;
          }

          .tg-btn-primary {
            background:#ffffff;
            color:var(--tg-primary-blue);
            box-shadow:0 12px 28px rgba(0,0,0,0.16);
          }

          .tg-btn-primary:hover {
            transform:translateY(-1px);
            box-shadow:0 14px 34px rgba(0,0,0,0.22);
          }

          .tg-btn-ghost {
            background:rgba(255,255,255,0.06);
            border-color:rgba(255,255,255,0.42);
            color:#ffffff;
          }

          .tg-hero-note {
            font-size:13px;
            color:rgba(255,255,255,0.9);
          }

          .tg-logo-row {
            display:flex;
            align-items:center;
            gap:10px;
            margin-bottom:10px;
          }

          .tg-logo-row img {
            width:50px;
            height:50px;
            border-radius:18px;
            background:#ffffff;
            object-fit:contain;
          }

          .tg-logo-row span {
            font-weight:700;
            letter-spacing:0.06em;
            font-size:13px;
            text-transform:uppercase;
            opacity:0.86;
          }

          .tg-powered-by {
            font-size:11px;
            color:rgba(255,255,255,0.8);
            font-weight:500;
            margin-top:4px;
          }

          .tg-hero-phone {
            display:flex;
            justify-content:center;
            align-items:center;
          }

          .tg-hero-phone img {
            width:260px;
            max-width:100%;
            border-radius:40px;
            box-shadow:0 40px 80px rgba(0,0,0,0.45);
          }

          .tg-section {
            margin-top:40px;
          }

          .tg-section-header {
            margin-bottom:18px;
          }

          .tg-kicker {
            text-transform:uppercase;
            font-size:12px;
            color:var(--tg-primary-blue);
            letter-spacing:0.08em;
            font-weight:700;
            margin-bottom:4px;
          }

          .tg-section h2 {
            margin:0 0 6px;
            font-size:24px;
            color:var(--tg-primary);
          }

          .tg-section-subtitle {
            margin:0;
            font-size:14px;
            color:var(--tg-muted);
            max-width:720px;
          }

          .tg-two-col {
            display:grid;
            grid-template-columns:minmax(0,1.4fr) minmax(0,1.2fr);
            gap:24px;
            align-items:flex-start;
          }

          @media (max-width: 900px) {
            .tg-two-col {
              grid-template-columns:1fr;
            }
          }

          .tg-card {
            background:var(--tg-card-bg);
            border-radius:var(--tg-radius-lg);
            padding:18px 18px 16px;
            box-shadow:0 14px 34px rgba(11,25,60,0.12);
          }

          .tg-card h3 {
            margin:0 0 8px;
            font-size:18px;
            color:var(--tg-primary);
          }

          .tg-card p {
            margin:0 0 8px;
            font-size:14px;
            color:var(--tg-text);
          }

          .tg-card small {
            font-size:12px;
            color:var(--tg-muted);
          }

          .tg-feature-grid {
            display:grid;
            grid-template-columns:repeat(3,minmax(0,1fr));
            gap:16px;
            margin-top:10px;
          }

          @media (max-width: 1000px) {
            .tg-feature-grid {
              grid-template-columns:repeat(2,minmax(0,1fr));
            }
          }

          @media (max-width: 640px) {
            .tg-feature-grid {
              grid-template-columns:1fr;
            }
          }

          .tg-feature-card {
            background:#ffffff;
            border-radius:var(--tg-radius-md);
            padding:13px 13px 11px;
            border:1px solid rgba(12,42,90,0.06);
          }

          .tg-feature-title {
            font-size:14px;
            font-weight:700;
            margin-bottom:4px;
            color:var(--tg-primary);
          }

          .tg-feature-text {
            font-size:13px;
            color:var(--tg-muted);
            line-height:1.5;
          }

          .tg-cols-audience {
            display:grid;
            grid-template-columns:repeat(2,minmax(0,1fr));
            gap:20px;
            margin-top:16px;
          }

          @media (max-width: 900px) {
            .tg-cols-audience {
              grid-template-columns:1fr;
            }
          }

          .tg-list-check {
            list-style:none;
            margin:0;
            padding:0;
            font-size:14px;
          }

          .tg-list-check li {
            display:flex;
            gap:8px;
            margin-bottom:7px;
            color:var(--tg-text);
          }

          .tg-check {
            width:17px;
            height:17px;
            border-radius:50%;
            border:2px solid var(--tg-primary-blue);
            display:inline-flex;
            align-items:center;
            justify-content:center;
            font-size:11px;
            color:var(--tg-primary-blue);
            flex-shrink:0;
            margin-top:1px;
          }

          .tg-gallery-grid {
            display:grid;
            grid-template-columns:repeat(3,minmax(0,1fr));
            gap:20px;
            margin-top:18px;
          }

          @media (max-width: 1024px) {
            .tg-gallery-grid {
              grid-template-columns:repeat(2,minmax(0,1fr));
            }
          }

          @media (max-width: 700px) {
            .tg-gallery-grid {
              grid-template-columns:1fr;
            }
          }

          .tg-gallery-item {
            background:#ffffff;
            border-radius:26px;
            padding:10px 10px 14px;
            text-align:center;
            box-shadow:0 12px 28px rgba(0,0,0,0.14);
          }

          .tg-gallery-item img {
            width:100%;
            border-radius:24px;
            display:block;
          }

          .tg-gallery-caption {
            margin-top:8px;
            font-size:13px;
            color:var(--tg-muted);
          }

          .tg-qr-layout {
            display:grid;
            grid-template-columns:minmax(0,1.4fr) minmax(0,1.1fr);
            gap:22px;
            align-items:stretch;
          }

          @media (max-width: 900px) {
            .tg-qr-layout {
              grid-template-columns:1fr;
            }
          }

          .tg-step-list {
            margin:0;
            padding-left:20px;
            font-size:14px;
            color:var(--tg-text);
          }

          .tg-step-list li {
            margin-bottom:8px;
          }

          .tg-step-label {
            font-weight:600;
            color:var(--tg-primary);
          }

          .tg-qr-card {
            background:#ffffff;
            border-radius:22px;
            padding:18px 18px 16px;
            border:1px dashed rgba(15,106,245,0.55);
            text-align:center;
          }

          .tg-qr-title {
            font-size:18px;
            margin:0 0 4px;
            color:var(--tg-primary);
          }

          .tg-qr-subtitle {
            font-size:14px;
            margin:0 0 10px;
            color:var(--tg-muted);
          }

          .tg-qr-image-wrap {
            margin:8px 0 6px;
            display:flex;
            flex-direction:column;
            align-items:center;
            gap:10px;
          }

          .tg-qr-image-wrap img {
            width:220px;
            max-width:70vw;
            border-radius:20px;
            border:1px solid rgba(0,0,0,0.06);
            background:#ffffff;
          }

          .tg-small-note {
            font-size:12px;
            color:var(--tg-muted);
            margin-top:6px;
          }

          .tg-faq-grid {
            display:grid;
            grid-template-columns:repeat(2,minmax(0,1fr));
            gap:18px;
            margin-top:10px;
          }

          @media (max-width: 900px) {
            .tg-faq-grid {
              grid-template-columns:1fr;
            }
          }

          .tg-faq-item h3 {
            margin:0 0 4px;
            font-size:15px;
            color:var(--tg-primary);
          }

          .tg-faq-item p {
            margin:0;
            font-size:13px;
            color:var(--tg-muted);
          }

          .tg-disclaimer {
            margin-top:18px;
            padding:12px 14px;
            background:#fff7e5;
            border-radius:12px;
            border:1px solid #f4c86c;
            font-size:12px;
            color:#7b5a16;
          }

          .tg-contact-box {
            background:var(--tg-bg);
            border-radius:20px;
            padding:18px 18px 14px;
            display:grid;
            grid-template-columns:minmax(0,1.5fr) minmax(0,1.1fr);
            gap:18px;
            align-items:center;
            margin-top:14px;
          }

          @media (max-width: 900px) {
            .tg-contact-box {
              grid-template-columns:1fr;
            }
          }

          .tg-contact-box p {
            margin:3px 0;
            font-size:13px;
          }

          .tg-footer {
            margin-top:24px;
            font-size:11px;
            color:var(--tg-muted);
            text-align:center;
          }

          html { scroll-behavior:smooth; }

          @media print {
            .print\\:hidden {
              display: none !important;
            }
            body.print-mode .tg-page > *:not(.print-only) {
              display: none !important;
            }
            body.print-mode .print-only {
              display: block !important;
            }
            .tg-page {
              padding: 20px;
            }
            .tg-hero {
              break-inside: avoid;
            }
            .tg-card {
              break-inside: avoid;
            }
          }
          
          .print-only {
            display: none;
          }
        `}</style>

        {/* Back to Dashboard Button */}
        <div className="print:hidden" style={{padding: '20px 20px 0 20px', maxWidth: '1200px', margin: '0 auto'}}>
          <button
            onClick={() => navigate('/dashboard')}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 20px',
              background: '#ffffff',
              color: '#0f6af5',
              border: '2px solid #0f6af5',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#0f6af5';
              e.currentTarget.style.color = '#ffffff';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#ffffff';
              e.currentTarget.style.color = '#0f6af5';
            }}
          >
            <ArrowLeft style={{width: '16px', height: '16px'}} />
            Back to Dashboard
          </button>
        </div>

        {/* HERO */}
        <section className="tg-hero" id="top">
          <div>
            <div className="tg-logo-row">
              <img src="/tooth-guides/logo.png" alt="Tooth Guides logo" />
              <div>
                <span>Tooth Guides</span>
                <div className="tg-powered-by">Powered by PractCom</div>
              </div>
            </div>

            <h1 className="tg-hero-title">Your Complete Dental Health Companion</h1>
            <p className="tg-hero-subtitle">Dentist-created guidance that your patients can keep in their pocket.</p>
            <p className="tg-hero-text">
              Tooth Guides helps patients understand emergencies, common problems, treatments, and prevention in clear,
              friendly language. Dentists can share it as a free "after-hours helper" that cuts confusion, repeat questions,
              and needless panic.
            </p>

            <p className="tg-hero-note">
              Tooth Guides is free to use. To run it, patients first install the free <strong>OnSpace</strong> app —
              then Tooth Guides opens inside OnSpace on their phone.
            </p>
          </div>

          <div className="tg-hero-phone">
            <img src="/tooth-guides/splash-10in.png" alt="Tooth Guides splash screen on phone" />
          </div>
        </section>

        {/* VIDEO DEMO SECTION */}
        <section className="tg-section print:hidden" style={{background: '#f8fafc', paddingTop: '60px', paddingBottom: '60px'}}>
          <div style={{maxWidth: '1200px', margin: '0 auto', padding: '0 20px'}}>
            <div className="tg-section-header" style={{marginBottom: '40px'}}>
              <div className="tg-kicker">See It In Action</div>
              <h2>How Tooth Guides Works</h2>
              <p className="tg-section-subtitle">
                Watch this quick demo to see how Tooth Guides helps patients navigate dental emergencies and everyday dental questions.
              </p>
            </div>
            
            <div style={{
              maxWidth: '900px',
              margin: '0 auto',
              borderRadius: '16px',
              overflow: 'hidden',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
              background: '#000000'
            }}>
              <video 
                controls 
                style={{
                  width: '100%',
                  display: 'block',
                  maxHeight: '600px'
                }}
                poster="/tooth-guides/logo.png"
              >
                <source src="/tooth-guides/tooth-guides-demo.mp4" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        </section>

        {/* WHAT THE APP DOES */}
        <section className="tg-section">
          <div className="tg-section-header">
            <div className="tg-kicker">Overview</div>
            <h2>What Tooth Guides Does</h2>
            <p className="tg-section-subtitle">
              Tooth Guides is a structured library of dentist-approved guidance, AI-assisted answers, and simple explanations
              that patients can check before, after, and between visits — in English, Spanish, Chinese, Arabic, and French.
            </p>
          </div>

          <div className="tg-two-col">
            <div>
              <div className="tg-card">
                <h3>For Patients</h3>
                <p>
                  When something suddenly hurts, bleeds, breaks, or just feels "off," most people go straight to a search
                  engine and end up more anxious than before. Tooth Guides gives them calm, step-by-step information so
                  they know what to do next and what to ask you at their appointment.
                </p>
                <ul className="tg-list-check">
                  <li><span className="tg-check">✓</span><span>Immediate guidance for dental emergencies with clear urgency levels.</span></li>
                  <li><span className="tg-check">✓</span><span>Simple explanations for common conditions and treatments.</span></li>
                  <li><span className="tg-check">✓</span><span>Prevention tips and everyday care routines to keep problems from returning.</span></li>
                  <li><span className="tg-check">✓</span><span>AI assistant for "I can't find my question" moments.</span></li>
                  <li><span className="tg-check">✓</span><span>Multilingual experience so the whole family can understand in their own language.</span></li>
                </ul>
              </div>
            </div>

            <div>
              <div className="tg-card">
                <h3>For Dentists & Dental Teams</h3>
                <p>
                  Tooth Guides becomes your practice's free "digital handout" and after-hours companion. Share it at
                  check-in, on your website, in post-op emails, or by placing the QR code in your reception area.
                </p>
                <ul className="tg-list-check">
                  <li><span className="tg-check">✓</span><span>Reduces repetitive "is this normal?" calls and messages.</span></li>
                  <li><span className="tg-check">✓</span><span>Helps patients triage when to seek urgent care vs. when it can wait.</span></li>
                  <li><span className="tg-check">✓</span><span>Supports informed consent and understanding by explaining treatments in plain language.</span></li>
                  <li><span className="tg-check">✓</span><span>Reinforces your prevention messages between visits.</span></li>
                  <li><span className="tg-check">✓</span><span>Positions your practice as tech-forward and patient-focused — at zero cost to them.</span></li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* CORE FEATURES */}
        <section className="tg-section">
          <div className="tg-section-header">
            <div className="tg-kicker">Features</div>
            <h2>Inside the Tooth Guides App</h2>
            <p className="tg-section-subtitle">
              The app is organized so patients can quickly tap into the exact type of help they need — emergencies, everyday
              problems, treatments, prevention, dictionary, or AI assistant.
            </p>
          </div>

          <div className="tg-feature-grid">
            <div className="tg-feature-card">
              <div className="tg-feature-title">Emergency Guidance</div>
              <div className="tg-feature-text">
                Tap "Emergencies" to see issues like broken teeth, swelling, bleeding, trauma and more — each labeled with
                urgency and clear "what to do now" steps.
              </div>
            </div>
            <div className="tg-feature-card">
              <div className="tg-feature-title">Common Problems Library</div>
              <div className="tg-feature-text">
                Conditions such as sensitivity, cavities, grinding, cracked cusps, and many others are explained in simple,
                patient-friendly language.
              </div>
            </div>
            <div className="tg-feature-card">
              <div className="tg-feature-title">AI Assistant</div>
              <div className="tg-feature-text">
                If patients can't find their exact question, they tap "Ask AI" to get a tailored explanation guided by the
                same structure and tone as the Tooth Guides library.
              </div>
            </div>
            <div className="tg-feature-card">
              <div className="tg-feature-title">Treatment Explanations</div>
              <div className="tg-feature-text">
                Restorations, root canals, crowns, implants and more are described in clear, realistic terms that support
                what you've already told them in the chair.
              </div>
            </div>
            <div className="tg-feature-card">
              <div className="tg-feature-title">Prevention & Care</div>
              <div className="tg-feature-text">
                Dedicated section for prevention topics: children's dental care, daily routines, diet, pregnancy oral care,
                and professional care like fluoride and cleanings.
              </div>
            </div>
            <div className="tg-feature-card">
              <div className="tg-feature-title">Dental Dictionary</div>
              <div className="tg-feature-text">
                A searchable dictionary of dental terms — so when patients see "abscess," "bruxism," or "alveolar bone,"
                they can quickly understand what it means and where it fits.
              </div>
            </div>
            <div className="tg-feature-card">
              <div className="tg-feature-title">5 Languages</div>
              <div className="tg-feature-text">
                English, Spanish, Chinese, Arabic, and French are available from a "Choose Your Language" screen so the
                app feels truly usable for more families.
              </div>
            </div>
            <div className="tg-feature-card">
              <div className="tg-feature-title">Clean, Mobile-First Design</div>
              <div className="tg-feature-text">
                Large buttons, simple icons, and easy navigation work well for all ages — from teens to seniors on a phone
                screen.
              </div>
            </div>
            <div className="tg-feature-card">
              <div className="tg-feature-title">Always With Them</div>
              <div className="tg-feature-text">
                Once installed in OnSpace, Tooth Guides lives on the patient's phone — ready on weekends, vacations, and
                after hours when your office is closed.
              </div>
            </div>
          </div>
        </section>

        {/* SCREEN GALLERY */}
        <section className="tg-section">
          <div className="tg-section-header">
            <div className="tg-kicker">Preview</div>
            <h2>See Tooth Guides in Action</h2>
            <p className="tg-section-subtitle">
              These screenshots show the actual experience your patients will see after they scan the QR code and open
              Tooth Guides inside OnSpace.
            </p>
          </div>

          <div className="tg-gallery-grid">
            <div className="tg-gallery-item">
              <img src="/tooth-guides/splash-chromebook.png" alt="Tooth Guides home screen" />
              <div className="tg-gallery-caption">Home screen with Emergency Guidance, AI Assistant, and language choice.</div>
            </div>
            <div className="tg-gallery-item">
              <img src="/tooth-guides/choose-language.png" alt="Choose your language screen" />
              <div className="tg-gallery-caption">Patients select English, Spanish, Chinese, Arabic, or French.</div>
            </div>
            <div className="tg-gallery-item">
              <img src="/tooth-guides/dental-emergencies.png" alt="Dental emergencies list" />
              <div className="tg-gallery-caption">Dental emergencies sorted by urgency with step-by-step instructions.</div>
            </div>
            <div className="tg-gallery-item">
              <img src="/tooth-guides/common-problems.png" alt="Common problems list" />
              <div className="tg-gallery-caption">Common problems explained in concise, understandable language.</div>
            </div>
            <div className="tg-gallery-item">
              <img src="/tooth-guides/prevention.png" alt="Prevention and care list" />
              <div className="tg-gallery-caption">Prevention &amp; Care topics to reinforce your hygiene instructions.</div>
            </div>
            <div className="tg-gallery-item">
              <img src="/tooth-guides/dictionary.png" alt="Tooth Guides dictionary" />
              <div className="tg-gallery-caption">Dental dictionary that turns scary terminology into clear, simple descriptions.</div>
            </div>
          </div>
        </section>

        {/* FOR DENTISTS SECTION (DEEPER MARKETING) */}
        <section className="tg-section" id="for-dentists">
          <div className="tg-section-header">
            <div className="tg-kicker">For Dental Practices</div>
            <h2>Give Your Patients a Free App They'll Actually Use</h2>
            <p className="tg-section-subtitle">
              Tooth Guides is designed to make your life easier while making your practice look amazing. You can position it
              as a complimentary benefit of being a patient in your office — with zero cost to the patient and no technical
              setup on your end.
            </p>
          </div>

          <div className="tg-two-col">
            <div className="tg-card">
              <h3>Practice Benefits</h3>
              <ul className="tg-list-check">
                <li><span className="tg-check">✓</span><span><strong>Lower call volume &amp; clearer calls.</strong> Patients arrive already understanding the basics of their situation and why they're coming in.</span></li>
                <li><span className="tg-check">✓</span><span><strong>Better prepared patients.</strong> They know what a procedure is trying to achieve and what questions they want to ask.</span></li>
                <li><span className="tg-check">✓</span><span><strong>Fewer "Dr. Google" disasters.</strong> When patients read reliable information first, you spend less time correcting myths.</span></li>
                <li><span className="tg-check">✓</span><span><strong>Stronger loyalty &amp; trust.</strong> You're the dentist who didn't just treat them — you gave them a tool they can use 24/7.</span></li>
                <li><span className="tg-check">✓</span><span><strong>Great for multi-language offices.</strong> Tooth Guides can help bridge language gaps when chair-time is tight.</span></li>
              </ul>
            </div>

            <div className="tg-card">
              <h3>Simple Ways to Share Tooth Guides</h3>
              <p style={{marginBottom: '6px'}}>You can start using Tooth Guides in your practice today with just a QR code:</p>
              <ul className="tg-list-check">
                <li><span className="tg-check">✓</span><span>Place the QR code on a small sign at reception and in each operatory.</span></li>
                <li><span className="tg-check">✓</span><span>Add the QR image and a short explanation to your website and email signature.</span></li>
                <li><span className="tg-check">✓</span><span>Include it with post-treatment instructions: "Scan this if you have questions tonight."</span></li>
              </ul>
              <p style={{marginTop: '8px', fontSize: '13px', color: 'var(--tg-muted)'}}>
                Suggested script for your team:<br/>
                <em>"Before you leave, could you please scan this free Tooth Guides app? It gives you emergency tips, a dental
                dictionary, and answers if you're worried after hours."</em>
              </p>
            </div>
          </div>
        </section>

        {/* INSTALL / QR SECTION */}
        <section className="tg-section" id="get-app">
          <div className="tg-section-header">
            <div className="tg-kicker">Install Instructions</div>
            <h2>How to Get Tooth Guides on Your Phone</h2>
            <p className="tg-section-subtitle">
              Tooth Guides runs inside the free <strong>OnSpace</strong> app. Patients install OnSpace once, then Tooth
              Guides is available whenever they need it.
            </p>
          </div>

          <div className="tg-qr-layout">
            <div>
              <div className="tg-card">
                <h3>Quick Setup – For Patients</h3>
                <ol className="tg-step-list">
                  <li>
                    <span className="tg-step-label">Scan the QR code on this page or at your dentist's office.</span><br/>
                    Use the camera on your phone or a QR scanner app.
                  </li>
                  <li>
                    <span className="tg-step-label">Don't have the OnSpace AI app?</span><br/>
                    Your phone will automatically send you to the Apple App Store or Google Play so you can install the free
                    OnSpace app first.
                  </li>
                  <li>
                    <span className="tg-step-label">Open OnSpace to see Tooth Guides in action.</span><br/>
                    After OnSpace installs, the QR link opens Tooth Guides inside OnSpace. From now on, just open OnSpace and
                    tap Tooth Guides whenever you need help.
                  </li>
                </ol>

                <p className="tg-small-note" style={{marginTop: '10px'}}>
                  You can also search for "OnSpace" in your app store, install it, then search for "Tooth Guides" inside the
                  OnSpace app if you prefer not to scan a code.
                </p>
              </div>

              {/* Next Step */}
              <div style={{marginTop: '24px', textAlign: 'center'}}>
                <h3 style={{fontSize: '18px', margin: '0 0 8px', color: 'var(--tg-primary)', fontWeight: '600'}}>
                  Next Step
                </h3>
                <p style={{fontSize: '14px', margin: '0', color: 'var(--tg-text)', lineHeight: '1.5'}}>
                  Share the QR code with your next patient, or scan it yourself and explore the app as if you were a patient.
                  In just a few taps, you'll see how Tooth Guides fits into your everyday workflow.
                </p>
              </div>
            </div>

            <div className="tg-qr-card">
              <h3 className="tg-qr-title">Scan to Get Started</h3>
              <p className="tg-qr-subtitle">Use your phone camera to scan</p>

              <div className="tg-qr-image-wrap">
                <img src="/tooth-guides/qr-tooth-guides.png" alt="QR code to open Tooth Guides in OnSpace" />
              </div>

              <p className="tg-small-note">
                How to preview:<br/>
                1. Scan the QR code with your phone camera.<br/>
                2. Don't have the OnSpace AI app? We'll take you to the App Store / Google Play.<br/>
                3. Open OnSpace to see your app in action.
              </p>

              <p className="tg-small-note" style={{marginTop: '10px'}}>
                Tooth Guides itself is free. The only requirement is that it runs inside the free OnSpace app on your device.
              </p>
            </div>
          </div>
        </section>

        {/* EMAIL TO PATIENTS SECTION */}
        <section className="tg-section print:hidden">
          <div className="tg-section-header">
            <div className="tg-kicker">Send to Patients</div>
            <h2>Email Tooth Guides to Your Patients</h2>
            <p className="tg-section-subtitle">
              Select patients from your database and send them the Tooth Guides information via email.
            </p>
          </div>

          <div style={{textAlign: 'center'}}>
            <button
              onClick={() => setShowEmailModal(true)}
              style={{
                padding: '16px 32px',
                background: '#0f6af5',
                color: '#ffffff',
                border: 'none',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '10px',
                boxShadow: '0 4px 12px rgba(15, 106, 245, 0.3)'
              }}
            >
              <Mail style={{width: '20px', height: '20px'}} />
              Open Patient Selection
            </button>
          </div>
        </section>

        {/* PRINT ONLY SECTION */}
        <div className="print-only">
          <div style={{textAlign: 'center', marginBottom: '30px'}}>
            <img src="/tooth-guides/logo.png" alt="Tooth Guides" style={{width: '80px', margin: '0 auto 10px'}} />
            <h1 style={{fontSize: '28px', margin: '0 0 5px', color: '#1b2433'}}>Tooth Guides</h1>
            <p style={{fontSize: '14px', margin: 0, color: '#7b8698'}}>Powered by PractCom</p>
          </div>

          <div style={{textAlign: 'center', marginBottom: '30px'}}>
            <img src="/tooth-guides/qr-tooth-guides.png" alt="Scan to get Tooth Guides" style={{width: '300px', margin: '0 auto', border: '2px solid #e5e7eb', borderRadius: '16px', padding: '20px'}} />
            <p style={{fontSize: '16px', fontWeight: '600', margin: '15px 0 5px', color: '#1b2433'}}>Scan to Get Started</p>
            <p style={{fontSize: '14px', color: '#7b8698'}}>Use your phone camera to scan this QR code</p>
          </div>

          <div style={{background: '#f5f8ff', padding: '20px', borderRadius: '12px', marginBottom: '20px'}}>
            <h3 style={{fontSize: '18px', margin: '0 0 15px', color: '#1b2433'}}>How to Install (3 Easy Steps):</h3>
            <ol style={{margin: 0, paddingLeft: '20px', lineHeight: '1.8'}}>
              <li><strong>Scan the QR code</strong> above with your phone camera</li>
              <li><strong>Install the free OnSpace app</strong> (if you don't have it yet)</li>
              <li><strong>Open OnSpace</strong> and start using Tooth Guides!</li>
            </ol>
          </div>

          <div style={{borderTop: '2px solid #e5e7eb', paddingTop: '15px', textAlign: 'center'}}>
            <p style={{fontSize: '12px', color: '#7b8698', margin: 0}}>
              For questions, contact support@practcom.com
            </p>
          </div>
        </div>

        {/* FAQ / DISCLAIMER */}
        <section className="tg-section">
          <div className="tg-section-header">
            <div className="tg-kicker">FAQ</div>
            <h2>Common Questions</h2>
            <p className="tg-section-subtitle">
              A few quick clarifications you can share with patients or staff when introducing Tooth Guides.
            </p>
          </div>

          <div className="tg-faq-grid">
            <div className="tg-faq-item">
              <h3>Is Tooth Guides really free?</h3>
              <p>Yes. There is no fee for patients to download or use Tooth Guides inside OnSpace.</p>
            </div>
            <div className="tg-faq-item">
              <h3>Why is OnSpace required?</h3>
              <p>
                OnSpace is the platform that hosts Tooth Guides. Think of it as the "container" that delivers updates and
                keeps everything running smoothly on patients' phones.
              </p>
            </div>
            <div className="tg-faq-item">
              <h3>Does this replace my dentist?</h3>
              <p>
                No. Tooth Guides is an educational and support tool. It explains what might be happening and suggests safe
                next steps, but it never replaces an in-person examination or treatment plan from a licensed dentist.
              </p>
            </div>
            <div className="tg-faq-item">
              <h3>Can I customize Tooth Guides for my practice?</h3>
              <p>
                Tooth Guides is the same core app for all users. You can, however, pair it with your own printed or digital
                instructions and direct patients to use it as your recommended reference.
              </p>
            </div>
          </div>

          <div className="tg-disclaimer">
            <strong>Important Medical / Dental Notice:</strong> Tooth Guides is an educational resource only. It does not
            provide a diagnosis, prescribe treatment, or replace the advice of a licensed dentist or physician. If you
            experience severe pain, difficulty breathing or swallowing, facial swelling, trauma, uncontrolled bleeding, or
            any other sign of emergency, contact your dentist or local emergency services immediately.
          </div>
        </section>

        {/* CONTACT / SUPPORT */}
        <section className="tg-section">
          <div className="tg-section-header">
            <div className="tg-kicker">Support</div>
            <h2>Need Help Getting Started?</h2>
            <p className="tg-section-subtitle">
              Whether you're a dentist rolling out Tooth Guides to your patients or a patient trying it for the first time,
              we're happy to help.
            </p>
          </div>

          <div className="tg-contact-box">
            <div>
              <p><strong>Email:</strong> <a href="mailto:support@practcom.com" style={{color: 'var(--tg-primary-blue)'}}>support@practcom.com</a></p>
              <p style={{marginTop: '6px', fontSize: '12px', color: 'var(--tg-muted)'}}>
                Questions about Tooth Guides, suggestions for new topics, or feedback from your patients are always welcome.
              </p>
            </div>
          </div>
        </section>

        <div className="tg-footer">
          © {new Date().getFullYear()} Tooth Guides. Powered by PractCom. Educational tool only; not a substitute for professional dental care.
        </div>
      </div>

      {/* Email Patient Selection Modal */}
      {showEmailModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '20px'
        }}
        onClick={() => setShowEmailModal(false)}>
          <div 
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#ffffff',
              borderRadius: '20px',
              padding: '32px',
              maxWidth: '800px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
            }}>
            {/* Modal Header */}
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px'}}>
              <h2 style={{margin: 0, fontSize: '24px', color: '#1b2433'}}>
                Select Patients to Email
              </h2>
              <button
                onClick={() => setShowEmailModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '32px',
                  cursor: 'pointer',
                  color: '#6b7280',
                  lineHeight: 1,
                  padding: 0
                }}
              >
                ×
              </button>
            </div>

            {/* Filter Patients */}
            <div style={{marginBottom: '20px'}}>
              <label style={{display: 'block', fontSize: '14px', fontWeight: '600', color: '#1b2433', marginBottom: '8px'}}>
                Filter by name or email (optional)
              </label>
              <input
                type="text"
                placeholder="Type to filter patients..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '12px',
                  fontSize: '14px',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            {/* All Patients List */}
            <div style={{marginBottom: '20px'}}>
              <div style={{fontSize: '14px', fontWeight: '600', color: '#1b2433', marginBottom: '12px'}}>
                Select Patients ({selectedPatientsForEmail.length} selected)
              </div>
              
              {loadingPatients ? (
                <div style={{textAlign: 'center', padding: '40px', color: '#7b8698'}}>
                  Loading patients...
                </div>
              ) : filteredPatients.length === 0 ? (
                <div style={{textAlign: 'center', padding: '40px', color: '#7b8698'}}>
                  {searchTerm ? 'No patients found matching your filter' : 'No patients in database'}
                </div>
              ) : (
                <div style={{border: '1px solid #e5e7eb', borderRadius: '12px', maxHeight: '400px', overflowY: 'auto'}}>
                  {filteredPatients.map((patient) => {
                    const isSelected = selectedPatientsForEmail.some(p => p.id === patient.id);
                    return (
                      <div
                        key={patient.id}
                        style={{
                          padding: '12px 16px',
                          borderBottom: '1px solid #f3f4f6',
                          background: isSelected ? '#eff6ff' : '#ffffff',
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '12px'
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {
                            if (isSelected) {
                              setSelectedPatientsForEmail(selectedPatientsForEmail.filter(p => p.id !== patient.id));
                              const newTempEmails = {...tempEmails};
                              delete newTempEmails[patient.id];
                              setTempEmails(newTempEmails);
                            } else {
                              setSelectedPatientsForEmail([...selectedPatientsForEmail, patient]);
                            }
                          }}
                          style={{width: '18px', height: '18px', cursor: 'pointer', marginTop: '2px', flexShrink: 0}}
                        />
                        <div style={{flex: 1}}>
                          <div style={{fontWeight: '600', color: '#1b2433', fontSize: '14px', marginBottom: '4px'}}>
                            {patient.firstName} {patient.lastName}
                          </div>
                          {patient.email ? (
                            <div style={{color: '#7b8698', fontSize: '13px'}}>
                              {patient.email}
                            </div>
                          ) : (
                            <div>
                              <div style={{color: '#dc2626', fontSize: '12px', marginBottom: '6px'}}>
                                ⚠️ No email - add below:
                              </div>
                              <input
                                type="email"
                                placeholder="Enter email address..."
                                value={tempEmails[patient.id] || ''}
                                onChange={(e) => setTempEmails({...tempEmails, [patient.id]: e.target.value})}
                                disabled={!isSelected}
                                style={{
                                  width: '100%',
                                  padding: '8px 12px',
                                  border: '2px solid #e5e7eb',
                                  borderRadius: '8px',
                                  fontSize: '13px',
                                  outline: 'none',
                                  boxSizing: 'border-box',
                                  background: isSelected ? '#ffffff' : '#f9fafb'
                                }}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div style={{display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap'}}>
              <button
                onClick={handleSendEmailToSelected}
                disabled={selectedPatientsForEmail.length === 0 || emailSending}
                style={{
                  padding: '12px 24px',
                  background: selectedPatientsForEmail.length === 0 ? '#e5e7eb' : '#0f6af5',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: '600',
                  cursor: selectedPatientsForEmail.length === 0 ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <Mail style={{width: '16px', height: '16px'}} />
                {emailSending ? 'Sending...' : `Send to ${selectedPatientsForEmail.length} Selected`}
              </button>

              <button
                onClick={handleSendToAllWithEmail}
                disabled={emailSending}
                style={{
                  padding: '12px 24px',
                  background: emailSending ? '#e5e7eb' : '#10b981',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: '600',
                  cursor: emailSending ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <Mail style={{width: '16px', height: '16px'}} />
                {emailSending ? 'Sending...' : 'Send to All With Email'}
              </button>
              
              {selectedPatientsForEmail.length > 0 && (
                <button
                  onClick={() => {
                    setSelectedPatientsForEmail([]);
                    setTempEmails({});
                  }}
                  style={{
                    padding: '12px 24px',
                    background: '#ffffff',
                    color: '#6b7280',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Clear Selection
                </button>
              )}
            </div>

            {/* Status Messages */}
            {emailError && (
              <div style={{
                marginTop: '16px',
                padding: '12px',
                background: emailSuccess ? '#d1fae5' : '#fee2e2',
                color: emailSuccess ? '#065f46' : '#991b1b',
                borderRadius: '8px',
                fontSize: '14px'
              }}>
                {emailError}
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
}
